import ChatVoiceHint from "@mapmycustomers/shared/enum/chat/ChatVoiceHint";

import { VoiceTunables } from "./consts";
import isSpeech from "./isSpeech";

export interface HintState {
  hasSpoken: boolean;
  hint: ChatVoiceHint;
  peakRms: number;
  quietMs: number;
  speaking: boolean;
  totalMs: number;
}

export const initialHintState: HintState = {
  hasSpoken: false,
  hint: ChatVoiceHint.NONE,
  peakRms: 0,
  quietMs: 0,
  speaking: false,
  totalMs: 0,
};

// Pure so it can be tested without the Web Audio stack. Called once per frame with the RMS the
// analyser just computed, the time since the previous frame, and the session's resolved tunables.
export const reduceHint = (
  state: HintState,
  rms: number,
  dtMs: number,
  tunables: VoiceTunables
): HintState => {
  const speaking = isSpeech(rms, state.speaking, tunables);
  const totalMs = state.totalMs + dtMs;
  const peakRms = Math.max(state.peakRms, rms);
  const quietMs = speaking ? 0 : state.quietMs + dtMs;
  const hasSpoken = state.hasSpoken || speaking;

  // Checked first: a recording that captured nothing has nothing to submit, so nudging Done
  // would invite the rep to send an empty message.
  const noSignal =
    !hasSpoken && totalMs >= tunables.NO_SIGNAL_MS && peakRms < tunables.DEAD_MIC_RMS;

  // Deliberately scoped to the whole recording rather than a rolling window. A rolling window
  // would catch a mic muted mid-debrief, but with noiseSuppression on, a five-second thinking
  // pause and a muted mic look identical — and the case worth catching is the rep who could
  // never be heard at all.
  const done = hasSpoken && !speaking && quietMs >= tunables.DONE_HINT_SILENCE_MS;

  let hint = ChatVoiceHint.NONE;
  if (noSignal) {
    hint = ChatVoiceHint.NO_SIGNAL;
  } else if (done) {
    hint = ChatVoiceHint.DONE;
  }

  return { hasSpoken, hint, peakRms, quietMs, speaking, totalMs };
};
